import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { applyGlass, applyOverlay } from '../../theme/glassEffect';

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  ${applyOverlay()}
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
`;

const Modal = styled(motion.div)`
  ${applyGlass('darker')}
  width: 100%;
  max-width: 560px;
  overflow: hidden;
  position: relative;
`;

const Header = styled.div`
  padding: 40px 40px 24px;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 32px;
  right: 32px;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;

  &:hover {
    color: white;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const Title = styled.h2`
  font-size: 42px;
  color: #fff;
  margin: 0;
  font-weight: 400;
  text-align: center;
  letter-spacing: -0.02em;
`;

const Description = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.7);
  margin: 16px 0 0;
  text-align: center;
  line-height: 1.5;
  font-weight: 400;
`;

const Content = styled.div`
  padding: 32px 40px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const OptionRow = styled.div`
  ${applyGlass('dark')}
  padding: 24px;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.15);
  }
`;

const OptionContent = styled.div`
  display: flex;
  gap: 20px;
  align-items: flex-start;
`;

const Toggle = styled.div.attrs(props => ({
  onClick: !props.disabled ? props.onChange : undefined,
  role: 'switch',
  'aria-checked': props.checked,
  tabIndex: props.disabled ? -1 : 0
}))`
  width: 44px;
  height: 24px;
  background: ${props => props.checked ? '#5CD3FF' : 'rgba(255, 255, 255, 0.15)'};
  border-radius: 12px;
  position: relative;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:before {
    content: '';
    position: absolute;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: white;
    top: 3px;
    left: 3px;
    transition: transform 0.3s ease;
    transform: translateX(${props => props.checked ? '20px' : '0'});
  }

  &:hover {
    background: ${props => props.checked ? '#4CC3FF' : 'rgba(255, 255, 255, 0.2)'};
  }
`;

const TextContent = styled.div`
  flex: 1;
`;

const OptionTitle = styled.h3`
  font-size: 20px;
  font-weight: 500;
  color: white;
  margin: 0 0 12px 0;
`;

const OptionDescription = styled.p`
  font-size: 16px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
`;

const Footer = styled.div`
  padding: 24px 40px 40px;
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  position: relative;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 40px;
    right: 40px;
    height: 1px;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.1) 50%,
      rgba(255, 255, 255, 0) 100%
    );
  }
`;

const Button = styled.button`
  padding: ${props => props.primary ? '14px 32px' : '13px 32px'};
  border-radius: 8px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  letter-spacing: 0.02em;

  ${props => props.primary ? `
    background: #5CD3FF;
    color: #000;
    border: none;

    &:hover {
      background: #4CC3FF;
    }
  ` : `
    background: rgba(255, 255, 255, 0.03);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.1);

    &:hover {
      background: rgba(255, 255, 255, 0.06);
      border-color: rgba(255, 255, 255, 0.2);
    }
  `}
`;

const cookieOptions = [
  {
    id: 'essential',
    title: 'Essential Cookies',
    description: 'Enable basic functions like page navigation and access to secure areas of the website. Without these cookies, the website cannot function properly.',
    required: true
  },
  {
    id: 'analytics',
    title: 'Product analytics',
    description: 'Cookies are used to collect information about how visitors use the website. This information is used to improve the website and the user experience.',
    required: false
  }
];

const CookiePreferences = ({ isOpen, onClose, onSave }) => {
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false
  });

  const handleToggle = (id) => {
    if (id === 'essential') return;
    setPreferences(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleSave = () => {
    onSave(preferences);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <Overlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <Modal
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={e => e.stopPropagation()}
          >
            <Header>
              <Title>Cookie preferences</Title>
              <Description>
                We are using cookies to measure and improve your experience.
              </Description>
              <CloseButton onClick={onClose}>
                <X size={24} />
              </CloseButton>
            </Header>

            <Content>
              {cookieOptions.map(({ id, title, description, required }) => (
                <OptionRow key={id}>
                  <OptionContent>
                    <Toggle
                      checked={required || preferences[id]}
                      onChange={() => handleToggle(id)}
                      disabled={required}
                    />
                    <TextContent>
                      <OptionTitle>{title}</OptionTitle>
                      <OptionDescription>{description}</OptionDescription>
                    </TextContent>
                  </OptionContent>
                </OptionRow>
              ))}
            </Content>

            <Footer>
              <Button onClick={onClose}>CANCEL</Button>
              <Button primary onClick={handleSave}>
                SAVE SETTINGS
              </Button>
            </Footer>
          </Modal>
        </Overlay>
      )}
    </AnimatePresence>
  );
};

export default CookiePreferences; 